import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ModuleSwitcher from '@/components/ModuleSwitcher';
import InboxLink from '@/components/InboxLink';
import OrganizationBrand from '@/components/OrganizationBrand';
import {
  ArrowLeft,
  CalendarCheck,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileMusic,
  Files,
  Gauge,
  Library,
  LogOut,
  Menu,
  Music2,
  PenLine,
  Repeat,
  Settings,
  Users,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navigation = [
  // Module entry is separate from feature-level permissions.
  { name: 'Worship Dashboard', href: '/worship', icon: Gauge },
  { name: 'Planning', href: '/worship/planning', icon: CalendarDays },
  { name: 'Maandrooster', href: '/worship/roster', icon: CalendarCheck },
  { name: 'Mijn planning', href: '/worship/my-planning', icon: ClipboardList },
  { name: 'Setlists', href: '/worship/setlists', icon: FileMusic },
  { name: 'Setlistbibliotheek', href: '/worship/setlists/library', icon: Library, requiredCapability: 'worship.view_all' },
  { name: 'Liederen', href: '/worship/songs', icon: Music2 },
  { name: 'Songwriting Studio', href: '/worship/songwriting', icon: PenLine, requiredCapability: 'songwriting_access' },
  { name: 'Team', href: '/worship/team', icon: Users },
  { name: 'Dienstpatronen', href: '/worship/patterns', icon: Repeat },
  { name: 'Repetities', href: '/worship/rehearsals', icon: CalendarDays },
  { name: 'Bestanden', href: '/worship/files', icon: Files },
  { name: 'Instellingen', href: '/worship/settings', icon: Settings },
];

const WorshipLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasCapability, hasModule } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Navigatie sluiten"
          className="fixed inset-0 z-40 bg-slate-950/45 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="border-b border-slate-100 px-5 py-5">
          <div className="flex items-center justify-between">
            <Link to="/worship" className="flex min-w-0 items-center gap-3">
              <OrganizationBrand />
            </Link>
            <button type="button" className="text-slate-500 lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-600 text-white">
              <Music2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-400">Module</p>
              <p className="font-semibold text-slate-900">Worship Planning</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {navigation.filter((item) => {
            if (!hasModule('worship')) return false;
            return !item.requiredCapability || hasCapability(item.requiredCapability);
          }).map((item) => {
            // The most specific matching entry wins, so /worship/setlists/library does not also light up "Setlists".
            const matching = navigation.filter((entry) => (entry.href === '/worship' ? location.pathname === entry.href : location.pathname.startsWith(entry.href)));
            const active = matching.length > 0 && matching.reduce((best, entry) => (entry.href.length > best.href.length ? entry : best)).href === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors ${active ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-100 p-3">
          {!user?.restricted_to_worship && (
            <Link to="/dashboard" className="mb-2 flex min-h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" />
              Terug naar Zoea
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-slate-50">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-100 font-medium text-teal-800">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/worship/settings')}>
                <Settings className="mr-2 h-4 w-4" /> Instellingen
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-slate-200 bg-white/95 px-4 backdrop-blur sm:px-6 lg:px-8">
          <button type="button" aria-label="Navigatie openen" className="text-slate-600 lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <ModuleSwitcher />
          <InboxLink title="Inbox" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default WorshipLayout;
